/**
 * @file Damage that lands shoves what it hit (issue #75).
 *
 * The one place a body is pushed by something that hurt it. Bullets, hitscan
 * traces and bomber blasts all arrive here the same way — as a confirmed hit
 * carrying a direction and an impulse — so "how a shove works" is defined once
 * and a new damage source only has to describe its push in data.
 *
 * Nothing here decides whether a hit counts: it listens to
 * `Events.DAMAGE_APPLIED`, which `HealthSystem` only emits once immunity
 * filters, shields, i-frames and cheats have all had their say. A blocked or
 * fully-shielded hit therefore never pushes anything, without this system
 * knowing those rules exist.
 *
 * The push goes on `Velocity.knockback` rather than `Velocity.linear` because
 * steering drags `linear` back to whatever the controller asked for and clamps
 * it to the body's top speed every frame: a shove has to outrun that to be
 * visible. It is still integrated by `MovementSystem`, so collision resolution
 * runs afterwards as usual and a shoved body stops at walls.
 */

import { System } from '../ecs/System.js';
import { Velocity } from '../components/index.js';
import { Events } from '../core/EventBus.js';

export class KnockbackSystem extends System {
  /** Ahead of `MovementSystem` (40), so a hit lands in the same frame it happened. */
  static priority = 35;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DAMAGE_APPLIED, (payload) => this._onDamageApplied(payload))
    );
  }

  /** @returns {void} */
  dispose() {
    for (const off of this._subscriptions) off();
    this._subscriptions.length = 0;
  }

  /**
   * Applies whatever push the damage came with. Nothing here knows what kind of
   * damage it was: a source that wants to shove says so by putting a direction
   * and an impulse on the hit, and one that does not simply omits them. That is
   * what lets a bullet, a blast and anything added later share one mechanism
   * instead of each growing its own.
   *
   * @param {{ target: import('../ecs/Entity.js').Entity,
   *           direction?: { x: number, z: number } | null, impulse?: number }} payload
   *   Confirmed damage. For a bullet `direction` is the round's own travel
   *   direction, so the target is pushed along the shot's path rather than away
   *   from a shooter who may be nowhere near the line of fire; for a blast it
   *   points out from the centre.
   * @returns {void}
   * @private
   */
  _onDamageApplied({ target, direction, impulse = 0 }) {
    if (!direction || impulse <= 0) return;

    const velocity = target?.get?.(Velocity);
    if (!velocity) return;

    const length = Math.hypot(direction.x, direction.z);
    if (length < 1e-6) return;

    velocity.knockback.x += (direction.x / length) * impulse;
    velocity.knockback.z += (direction.z / length) * impulse;

    // Clamping the vector rather than budgeting per hit is what keeps a shotgun
    // blast from launching a body: five pellets saturate at the same ceiling one
    // pellet would reach, and a burst cannot stack across frames either. The
    // ceiling is shared by every push, so no source can outrun it.
    const { maxImpulse } = this.ctx.config.knockback;
    const speed = Math.hypot(velocity.knockback.x, velocity.knockback.z);
    if (speed > maxImpulse) {
      const scale = maxImpulse / speed;
      velocity.knockback.x *= scale;
      velocity.knockback.z *= scale;
    }
  }
}
